import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import { Box, Divider, OutlinedInput, Typography } from '@mui/material'

import { AlertDialog, FormLabel, MButton, ProgressLoaders } from 'components'
import { CLIENT } from 'config'
import { color } from 'config/color'
import { useAuth } from 'context'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useMobileBreakpoints } from 'utils'

export const PhoneNumber = (): JSX.Element => {
  const isMobile = useMobileBreakpoints()
  const isCarmel = process.env.REACT_APP_CLIENT_NAME === CLIENT.CARMEL
  const { account, updateInfo, getUser } = useAuth()
  const [editing, setEditing] = useState<boolean>(false)
  const [dialogData, setDialogData] = useState<any>({
    type: 'success',
    isOpen: false,
    title: 'Changes Saved',
    message: 'You have successfully update the customer’s phone number.'
  })

  const { handleSubmit, control, reset, formState } = useForm<{
    mobileNumber: string
  }>({
    mode: 'all'
  })

  useEffect(() => {
    if (account) {
      reset({
        mobileNumber: account?.mobileNumber
      })
    }
  }, [account, reset])

  const onSubmit = async (data: any): Promise<void> => {
    const { accountId, ccsEmail } = account
    const { mobileNumber } = data

    if (editing) {
      const defaultPayload = {
        accountId,
        email: ccsEmail
      }

      const phoneNumberPayload = {
        ...defaultPayload,
        updates: [
          {
            object: 'PORTALPHONE',
            value: mobileNumber
          }
        ]
      }

      try {
        await updateInfo(phoneNumberPayload)
        await getUser('', ccsEmail)
        setDialogData({ ...dialogData, isOpen: true })
      } catch (error) {
        await getUser('', ccsEmail)
        setDialogData({
          type: 'error',
          isOpen: true,
          title: 'An error occured',
          message: 'There is an error updating the customer\'s phone number.'
        })
      }
    }
    setEditing(!editing)
  }

  if (!account) {
    return (
      <Box border={`1px solid ${color.secondary.dark[300]}`} p={2}>
        <ProgressLoaders height="100%" />
      </Box>
    )
  }

  return (
    <Box border={`1px solid ${color.secondary.dark[300]}`}>
      <form method="post" onSubmit={handleSubmit(onSubmit)}>
        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          p={1}
          bgcolor={color.grey[50]}
          gap={isMobile ? 1 : 0}
        >
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <AccountCircleIcon
              sx={{
                color: color.grey[500],
                backgroundColor: color.grey[200],
                fontSize: 26,
                borderRadius: '50%',
                padding: 1
              }}
            />

            <Typography color={color.black} fontWeight={600} fontSize={16}>
              Phone Number
            </Typography>
          </Box>

          <Box
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            gap={1}
          >
            {!isCarmel && <MButton
              type="submit"
              variant="outlined"
              rounded="true"
              texttransform="none"
              fullWidth={isMobile}
              disabled={!account || formState.isSubmitting}
            >
              {editing ? 'Save Changes' : 'Edit Information'}
            </MButton>}
            {editing && (
              <MButton
                type="button"
                variant="outlined"
                rounded="true"
                texttransform="none"
                fullWidth={isMobile}
                onClick={() => {
                  setEditing(!editing)
                }}
              >
                Cancel
              </MButton>
            )}
          </Box>
        </Box>

        <Divider sx={{ color: color.secondary.dark[300] }} />
        <Box
          display="flex"
          justifyContent="space-between"
          flexDirection={isMobile ? 'column' : 'row'}
          px={2}
          py={1.5}
          bgcolor={color.white}
          gap={isMobile ? 1 : 0}
        >
          <Box display="flex" flexDirection="column" width="100%">
            <FormLabel textcolor={color.grey[600]}>
              Phone number
            </FormLabel>
            <Controller
              name="mobileNumber"
              control={control}
              render={({ field: { onChange, value, name } }): JSX.Element => (
                <OutlinedInput
                  name={name}
                  onChange={onChange}
                  value={value}
                  type="text"
                  fullWidth
                  disabled={!editing}
                  placeholder="(000) 000-0000"
                />
              )}
            />
          </Box>
        </Box>
      </form>

      <AlertDialog
        open={dialogData.isOpen}
        type={dialogData.type}
        title={dialogData.title}
        message={dialogData.message}
        handleClick={() => {
          setDialogData({ ...dialogData, isOpen: false })
        }}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          <MButton
            variant="contained"
            type="button"
            rounded="true"
            texttransform="none"
            onClick={() => {
              setDialogData({ ...dialogData, isOpen: false })
            }}
          >
            Go back
          </MButton>
        </Box>
      </AlertDialog>
    </Box>
  )
}
